import { useTranslation } from "react-i18next";
import * as z from "zod";
import { ArtMetadataSchema, type ArtMetadata } from "../../../lib/schedule-presets/art-metadata";
import { metadataReferences } from "../../../lib/schedule-presets/metadata-schema";
import { buildRenamedFormatMetadata, canRenameFormat } from "../../../lib/schedule-presets/rename-format";
import { useMetadataEditorNavigationStore } from "../../../stores/metadata-editor-navigation-store";
import { MetadataField } from "./MetadataField";
import { MetadataDictionaryEditor } from "./MetadataDictionaryEditor";
import { MetadataFormContext } from "./MetadataFormContext";
import { metadataLabels } from "./metadata-form-schema";

export function MetadataForm({ metadata, onChange, section }: {
  metadata: ArtMetadata;
  onChange: (value: ArtMetadata) => void;
  section: "elements" | "styles" | "formats";
}) {
  const { t } = useTranslation();
  const labels = metadataLabels(t);
  const allFields = Object.entries(ArtMetadataSchema.shape) as [string, z.ZodType][];
  const styleFields = ["styles", "formats"];
  const fields = allFields.filter(([key]) => section === "elements" ? !styleFields.includes(key) : key === section);
  const values = metadata as Record<string, unknown>;
  const references = metadataReferences(ArtMetadataSchema, metadata);
  const renameFormat = (name: string, nextName: string) => {
    if (name === nextName) return true;
    const validRename = canRenameFormat(metadata.formats, name, nextName);
    if (!validRename) return false;
    const next = buildRenamedFormatMetadata(metadata, references, name, nextName);
    onChange(next);
    const navigation = useMetadataEditorNavigationStore.getState();
    navigation.openFormat(nextName);
    return true;
  };

  return (
    <MetadataFormContext.Provider value={{ values, references, renameFormat }}>
      <div className="space-y-2 rounded-xl bg-neutral-100/70 p-2">
        {section !== "elements" ? (
          <MetadataDictionaryEditor
            schema={ArtMetadataSchema.shape[section]}
            value={metadata[section]}
            labels={labels}
            onChange={value => onChange({ ...metadata, [section]: value })}
          />
        ) : fields.map(([key, schema]) => (
          <MetadataField
            key={key}
            schema={schema}
            value={values[key]}
            label={labels[key] ?? key}
            labels={labels}
            onChange={value => {
              const next = { ...metadata, [key]: value };
              if (value === undefined) delete (next as Record<string, unknown>)[key];
              onChange(next);
            }}
          />
        ))}
      </div>
    </MetadataFormContext.Provider>
  );
}
